// MainApp.tsx
import "swiper/swiper-bundle.css";
import "normalize.css";
import "./utils/yupLocale";
import "tippy.js/dist/tippy.css";
import "react-dates/lib/css/_datepicker.css";
import "styles/App.scss";
import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import AppRouter from "app/router";
import { GuidedTour } from "components/GuidedTour/GuidedTour";
import { ProfileContextProvider } from "context/ProfileContext";
import { useEffect, useRef, useState } from "react";
import { ReCaptcha } from "react-recaptcha-google";
import styled from "styled-components";
import GlobalStyles from "styles/base";
import { theme } from "theme";

import { useAccessibilitySetup } from "newui/features/Auth/hooks/useAccessibilitySetup";
import { useGoogleAnalytics } from "newui/features/Auth/hooks/useGoogleAnalytics";
import { useRecaptcha } from "newui/features/Auth/hooks/useRecaptcha";
import { REDIRECT_LOGIN_PATHS, useRedirectHandler } from "newui/features/Auth/hooks/useRedirectHandler";
import { RfxContextProvider } from "newui/features/Rfx/context/RfxContext";
import { RfxSubmissionContextProvider } from "newui/features/Rfx/context/RfxSubmissionContext";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { Toast } from "components/Toast";
import { CustomToast } from "components/Toast/CustomToast";
import SessionTimeoutModal from "newui/features/Auth/components/SessionTimeoutModal/SessionTimeoutModal";
import { SessionEvent, useSessionBroadcast } from "newui/features/Auth/hooks/useSessionChannel";
import { getTabId } from "newui/features/Auth/constants/TabId";
import { useCurrentCompany, useCurrentProfileType, useCurrentUser } from "hooks/auth";
import { useAppSelector } from "newui/store/hooks";
import { selectAuthLoading } from "newui/features/Auth/authSelectors";
import { FileUploadProvider } from "newui/components/FileUpload/hooks/FileUploadContext";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";

const AppWrapper = styled.div`
	background-color: ${theme.colors.white};
	margin-left: 0;
	min-height: 100vh;
	height: 100%;

	${theme.mediaQueries.xxl} {
		margin-left: 0;
	}
`;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
		},
	},
});

const MainApp: React.FC = () => {
	const [showPopUp, setShowPopUp] = useState<boolean>(false);
	const recaptchaRef = useRef<ReCaptcha>(null);

	const { currentUser, isGuest } = useCurrentUser();
	const currentCompany = useCurrentCompany();
	const currentProfileType = useCurrentProfileType();

	const { sendSessionBroadcast } = useSessionBroadcast((msg) => {

		const currentTab = getTabId();
		const msgTab = msg.tabId;

		if (msgTab === currentTab) {
			getTabId(true);
		}

		return;
	});

	useEffect(() => {
		sendSessionBroadcast(SessionEvent.NEWTAB);
	}, [sendSessionBroadcast]);

	// Custom hooks handle all the complex logic
	const {
		isVerified,
		isSuspended,
	} = useAuthContext();
	const isAuthLoading = useAppSelector(selectAuthLoading);

	// this handles redirecting after login / verification
	if (!currentUser) {
		// If user is not logged in, capture current path
		const path = window.location.pathname;
		const isLoginPath = REDIRECT_LOGIN_PATHS.includes(path);
		if (!isLoginPath) {
			localStorage.setItem("redirect_path", path);
		}
	}

	useRedirectHandler({
		isVerified,
		role: currentProfileType,
		companyId: currentCompany?.id || null,
	});

	useAccessibilitySetup();
	useGoogleAnalytics();
	useRecaptcha();

	// Disable console.log in production
	if (process.env.NODE_ENV === "production") {
		console.log = (): void => { };
	}

	const handleAppWrapperClick = (): void => {
		if (showPopUp) {
			setShowPopUp(false);
		}
	};

	const renderContent = (): JSX.Element => {
		// Loading state
		if (isAuthLoading) {
			return (
				<>
					<LoadingSpinner />
				</>
			);
		}

		// Logged in
		if (currentUser) {
			return (
				<AppWrapper
					className="App"
					onClick={handleAppWrapperClick}
				>
					<AppRouter
						isLoggedIn={true}
						isAccountSuspended={isSuspended}
						isVerifiedMember={isVerified}
						isGuestLoggedIn={isGuest}
					/>
					{/* @ts-ignore */}
					<Toast />
					{/* @ts-ignore */}
					<CustomToast />
				</AppWrapper>
			);
		}

		// Not logged in
		return <AppRouter isLoggedIn={false} />;
	};

	return (
		<QueryClientProvider client={queryClient}>
			<ReactQueryDevtools
				initialIsOpen={false}
				buttonPosition="bottom-left"
			/>
			<ProfileContextProvider>
				<RfxContextProvider>
					<RfxSubmissionContextProvider>
						<GlobalStyles />
						<ReCaptcha
							ref={recaptchaRef}
							size="invisible"
							render="explicit"
							sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
						/>
						<SessionTimeoutModal />
						<FileUploadProvider>
							<GuidedTour>{renderContent()}</GuidedTour>
						</FileUploadProvider>
					</RfxSubmissionContextProvider>
				</RfxContextProvider>
			</ProfileContextProvider>
		</QueryClientProvider>
	);
};

export default MainApp;
